// v1.5.0 · Analytica Pro (Cruz Azul / DIFARE): análisis de mercado farmacéutico, CPI y posicionamiento (5 pantallas)
import React from 'react';
import { useTick, Count, Head, Live, Kpi, Status, wave, fmt } from './kit';

const HL = '#57D3FF';      // Cruz Azul (entidad resaltada)
const MUTE = '#5E7E90';    // competidores

/* 1 · CPI: índice de precio competitivo */
export function DifareCpi() {
  const t = useTick(1800);
  const cpi = 96.4 + wave(t, 0.7, 0.6);
  const ang = -90 + ((cpi - 80) / 40) * 180;
  const weeks = [101.8, 101.1, 100.4, 99.6, 99.9, 98.7, 98.1, 97.6, 97.9, 97.0, 96.6, 96.4];
  const x = (i) => 20 + i * (300 / 11);
  const y = (v) => 110 - (v - 95) * 14;
  const line = weeks.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1fr_1.25fr]">
      <div className="dash-panel flex min-h-0 flex-col p-3">
        <Head title="CPI, índice de precio competitivo" sub="Cruz Azul frente al promedio del mercado" right={<Live label="Actualizado" />} />
        <svg viewBox="0 0 220 128" className="mx-auto mt-2 h-[150px] w-full max-w-[280px]" role="img" aria-label={`CPI ${fmt(cpi, 1)}: más barato que el mercado`}>
          <path d="M20 112 A 90 90 0 0 1 110 22" fill="none" stroke="rgba(79,191,106,.55)" strokeWidth="14" />
          <path d="M110 22 A 90 90 0 0 1 200 112" fill="none" stroke="rgba(233,162,59,.45)" strokeWidth="14" />
          <g style={{ transform: `rotate(${ang}deg)`, transformOrigin: '110px 112px', transition: 'transform 1100ms cubic-bezier(.23,1,.32,1)' }}>
            <line x1="110" y1="112" x2="110" y2="34" stroke="#DDF0F7" strokeWidth="3" strokeLinecap="round" />
          </g>
          <circle cx="110" cy="112" r="6" fill="#DDF0F7" />
          <text x="20" y="126" fontSize="10" fill="#86A6B6">80</text>
          <text x="104" y="14" fontSize="10" fill="#86A6B6">100</text>
          <text x="188" y="126" fontSize="10" fill="#86A6B6">120</text>
        </svg>
        <div className="text-center"><span className="text-[34px] font-extrabold leading-none"><Count value={cpi} dec={1} /></span></div>
        <div className="mt-1 flex justify-center"><Status tone="ok">3,6 puntos más barato que el mercado</Status></div>
        <p className="dim mt-auto text-[11.5px] leading-snug">CPI = precio Cruz Azul ÷ precio promedio de la competencia × 100. Bajo 100, el cliente paga menos que en el mercado.</p>
      </div>
      <div className="dash-panel flex min-h-0 flex-col p-3">
        <Head title="Evolución del CPI" sub="Últimas 12 semanas, canasta de 1.240 productos" />
        <svg viewBox="0 0 340 140" className="mt-2 h-full min-h-[150px] w-full" preserveAspectRatio="none" role="img" aria-label="El CPI bajó de 101,8 a 96,4 en 12 semanas">
          {[96, 98, 100, 102].map((v) => (
            <g key={v}>
              <line x1="20" x2="320" y1={y(v)} y2={y(v)} stroke={v === 100 ? 'rgba(221,240,247,.4)' : 'rgba(221,240,247,.08)'} strokeDasharray={v === 100 ? '4 4' : ''} />
              <text x="0" y={y(v) + 3} fontSize="9" fill="#86A6B6">{v}</text>
            </g>
          ))}
          <path d={`${line} L ${x(11)} 130 L 20 130 Z`} fill="rgba(87,211,255,.10)" className="fade-in" style={{ '--d': '600ms' }} />
          <path d={line} fill="none" stroke={HL} strokeWidth="2" pathLength="1" className="draw" />
          <circle cx={x(11)} cy={y(96.4)} r="4" fill={HL} className="fade-in" style={{ '--d': '1200ms' }} />
          <text x={x(11) - 4} y={y(96.4) - 9} textAnchor="end" fontSize="10" fontWeight="700" fill="#DDF0F7" className="fade-in" style={{ '--d': '1300ms' }}>96,4</text>
          <text x="320" y={y(100) - 5} textAnchor="end" fontSize="9" fill="#86A6B6">paridad con el mercado</text>
        </svg>
      </div>
    </div>
  );
}

/* 2 · Mapa de posicionamiento de mercado */
const PLAYERS = [
  { n: 'Cruz Azul', cpi: 96.4, cov: 88, share: 31, us: true },
  { n: 'Competidor A', cpi: 104.2, cov: 79, share: 24 }, { n: 'Competidor B', cpi: 99.1, cov: 62, share: 17 },
  { n: 'Competidor C', cpi: 108.6, cov: 70, share: 14 }, { n: 'Competidor D', cpi: 93.5, cov: 48, share: 9 },
];
export function DifarePosicion() {
  const t = useTick(2200);
  const X = (c) => 40 + ((c - 88) / 24) * 400;
  const Y = (v) => 260 - ((v - 40) / 55) * 230;
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.7fr_1fr]">
      <div className="dash-panel relative min-h-0 overflow-hidden p-2">
        <svg viewBox="0 0 460 290" className="h-full w-full" role="img" aria-label="Mapa de posicionamiento: precio relativo frente a cobertura de surtido">
          <line x1={X(100)} x2={X(100)} y1="20" y2="262" stroke="rgba(221,240,247,.22)" strokeDasharray="4 4" />
          <line x1="40" x2="440" y1={Y(70)} y2={Y(70)} stroke="rgba(221,240,247,.22)" strokeDasharray="4 4" />
          <text x="48" y="34" fontSize="11" fontWeight="700" fill="#8BE07F">Líder de valor</text>
          <text x="432" y="34" textAnchor="end" fontSize="11" fontWeight="700" fill="#8CCBEA">Premium</text>
          <text x="48" y="254" fontSize="11" fontWeight="700" fill="#F2C266">Económico</text>
          <text x="432" y="254" textAnchor="end" fontSize="11" fontWeight="700" fill="#FF8F82">Rezagado</text>
          <text x="240" y="284" textAnchor="middle" fontSize="10" fill="#86A6B6">Precio relativo (CPI) →</text>
          <text x="12" y="150" fontSize="10" fill="#86A6B6" transform="rotate(-90 12 150)" textAnchor="middle">Cobertura de surtido →</text>
          {PLAYERS.map((p, i) => {
            const dx = wave(t, i + 1, 4); const dy = wave(t, i + 2.3, 3);
            return (
              <g key={p.n} className="move" style={{ transform: `translate(${X(p.cpi) + dx}px, ${Y(p.cov) + dy}px)` }}>
                <circle r={6 + p.share * 0.75} fill={p.us ? 'rgba(87,211,255,.28)' : 'rgba(94,126,144,.35)'} stroke={p.us ? HL : MUTE} strokeWidth={p.us ? 2 : 1} />
                {p.us && <circle r={6 + p.share * 0.75} fill="none" stroke={HL} className="ring" />}
                <text y={-(10 + p.share * 0.75)} textAnchor="middle" fontSize="11" fontWeight={p.us ? 800 : 600} fill="#DDF0F7">{p.n}</text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="dash-panel hidden flex-col gap-2.5 p-3 sm:flex">
        <Head title="Lectura del mapa" sub="Tamaño = participación de mercado" />
        {PLAYERS.map((p) => (
          <div key={p.n} className="flex items-center justify-between text-[12px]">
            <span className={p.us ? 'font-bold text-[#9FE6FF]' : ''}>{p.n}</span>
            <span className="num dim">CPI {fmt(p.cpi, 1)}</span>
            <span className="num font-semibold">{p.share} %</span>
          </div>
        ))}
        <p className="mt-auto rounded-md bg-[rgba(87,211,255,.1)] p-2 text-[11.5px] leading-snug">Cruz Azul ocupa el cuadrante de líder de valor: precio bajo el mercado con la mayor cobertura de surtido.</p>
      </div>
    </div>
  );
}

/* 3 · Competidor por competidor (ranking que se reordena) */
const CATS = ['Analgésicos', 'Vitaminas', 'Dermocosmética', 'Pediatría'];
export function DifareCompetidores() {
  const t = useTick(2400);
  const cat = CATS[t % CATS.length];
  const rows = PLAYERS.map((p, i) => ({ ...p, v: p.cpi + wave(t, i + 1.4, 3.2) }))
    .sort((a, b) => a.v - b.v);
  const W = (v) => ((v - 85) / 30) * 100;
  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <Head title="CPI por competidor" sub={`Categoría: ${cat}`} right={<Live label="Cambia cada categoría" />} />
      <div className="dash-panel relative min-h-0 flex-1 p-3">
        <div className="relative h-full">
          {PLAYERS.map((p) => {
            const rank = rows.findIndex((r) => r.n === p.n);
            const v = rows[rank].v;
            return (
              <div key={p.n} className="move absolute inset-x-0 flex items-center gap-3" style={{ transform: `translateY(${rank * 52}px)` }}>
                <span className={`w-[96px] shrink-0 truncate text-[12px] ${p.us ? 'font-bold text-[#9FE6FF]' : ''}`}>{p.n}</span>
                <div className="relative h-7 flex-1 rounded bg-white/5">
                  <div className="bar-live-x absolute inset-y-0 left-0 w-full rounded" style={{ transform: `scaleX(${W(v) / 100})`, background: p.us ? HL : MUTE }} />
                  <div className="absolute inset-y-0 border-l border-dashed border-white/40" style={{ left: `${W(100)}%` }} />
                </div>
                <span className="num w-12 shrink-0 text-right text-[13px] font-bold">{fmt(v, 1)}</span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="dim text-[11.5px]">Línea punteada: paridad (CPI 100). Más corto, más barato para el cliente.</p>
    </div>
  );
}

/* 4 · Elasticidad precio-demanda */
export function DifareElasticidad() {
  const t = useTick(900);
  const k = (Math.sin(t / 4) + 1) / 2;             // recorre la curva
  const price = 2.4 + k * 1.6;                      // USD
  const units = 1800 * Math.pow(price / 2.4, -1.8);
  const X = (p) => 40 + ((p - 2.2) / 2) * 380;
  const Y = (u) => 240 - ((u - 500) / 1500) * 210;
  const pts = Array.from({ length: 30 }, (_, i) => { const p = 2.2 + (i / 29) * 2; return [X(p), Y(1800 * Math.pow(p / 2.4, -1.8))]; });
  const d = pts.map(([a, b], i) => `${i ? 'L' : 'M'}${a.toFixed(1)} ${b.toFixed(1)}`).join(' ');
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.6fr_1fr]">
      <div className="dash-panel min-h-0 p-2">
        <svg viewBox="0 0 440 270" className="h-full w-full" role="img" aria-label="Curva de demanda: a mayor precio, menos unidades; elasticidad −1,8">
          {[600, 1000, 1400, 1800].map((u) => (
            <g key={u}><line x1="40" x2="420" y1={Y(u)} y2={Y(u)} stroke="rgba(221,240,247,.08)" /><text x="34" y={Y(u) + 3} textAnchor="end" fontSize="9" fill="#86A6B6">{fmt(u)}</text></g>
          ))}
          <path d={d} fill="none" stroke="#2E9BD6" strokeWidth="2.5" pathLength="1" className="draw" />
          <line x1={X(price)} x2={X(price)} y1={Y(units)} y2="240" stroke="rgba(87,211,255,.5)" strokeDasharray="3 3" />
          <line x1="40" x2={X(price)} y1={Y(units)} y2={Y(units)} stroke="rgba(87,211,255,.5)" strokeDasharray="3 3" />
          <circle cx={X(price)} cy={Y(units)} r="7" fill={HL} stroke="#071925" strokeWidth="2" />
          <text x="230" y="264" textAnchor="middle" fontSize="10" fill="#86A6B6">Precio de venta (USD) →</text>
        </svg>
      </div>
      <div className="flex min-h-0 flex-col gap-2">
        <Kpi label="Precio simulado"><Count value={price} dec={2} prefix="$ " dur={600} /></Kpi>
        <Kpi label="Unidades por semana"><Count value={units} dur={600} /></Kpi>
        <Kpi label="Elasticidad del producto" tone="warn">−1,8</Kpi>
        <p className="dim text-[11.5px] leading-snug">Producto elástico: cada 10 % de alza de precio reduce la demanda en cerca de 18 %. Conviene competir por precio.</p>
      </div>
    </div>
  );
}

/* 5 · Simulador de márgenes */
export function DifareSimulador() {
  const t = useTick(1100);
  const desc = Math.round(((Math.sin(t / 3) + 1) / 2) * 15);     // 0–15 %
  const units = 1000 * (1 + desc * 0.06);
  const margin = 32 - desc * 1.1;
  const profit = (units * 4.1 * margin) / 100;
  const base = (1000 * 4.1 * 32) / 100;
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.2fr_1fr]">
      <div className="dash-panel flex min-h-0 flex-col gap-4 p-3">
        <Head title="Simulador de márgenes" sub="Vitamina C 1 g x 30, IVA 0 %" right={<Status tone="info">IVA selectivo</Status>} />
        <div>
          <div className="mb-2 flex justify-between text-[12px]"><span>Descuento al público</span><span className="num font-bold">{desc} %</span></div>
          <div className="relative h-2 rounded-full bg-white/10">
            <div className="bar-live-x absolute inset-0 rounded-full bg-[#2E9BD6]" style={{ transform: `scaleX(${desc / 15})` }} />
            <div className="move absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-[#071925] bg-[#57D3FF]" style={{ left: `calc(${(desc / 15) * 100}% - 8px)` }} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Kpi label="Unidades estimadas"><Count value={units} dur={700} /></Kpi>
          <Kpi label="Margen bruto" tone={margin < 20 ? 'warn' : 'ok'}><Count value={margin} dec={1} suffix=" %" dur={700} /></Kpi>
        </div>
      </div>
      <div className="dash-panel flex min-h-0 flex-col p-3">
        <Head title="Utilidad semanal" sub="Actual frente a simulada" />
        <div className="flex flex-1 items-end justify-center gap-8 pt-4">
          {[['Actual', base, MUTE], ['Simulada', profit, profit >= base ? '#4FBF6A' : '#E9A23B']].map(([l, v, c]) => (
            <div key={l} className="flex h-full w-16 flex-col items-center justify-end">
              <span className="mb-1 text-[12px] font-bold"><Count value={v} prefix="$ " dur={700} /></span>
              <div className="flex h-[150px] w-full items-end"><div className="bar-live w-full rounded-t-[4px]" style={{ height: '100%', background: c, transform: `scaleY(${v / 1500})` }} /></div>
              <span className="dim mt-1.5 text-[11px]">{l}</span>
            </div>
          ))}
        </div>
        <p className="dim mt-2 text-[11.5px]">{profit >= base ? 'El volumen extra compensa el descuento.' : 'El descuento ya no se paga con volumen.'}</p>
      </div>
    </div>
  );
}

export const difareScreens = [
  { t: 'CPI', C: DifareCpi }, { t: 'Posicionamiento', C: DifarePosicion }, { t: 'Competidores', C: DifareCompetidores },
  { t: 'Elasticidad', C: DifareElasticidad }, { t: 'Simulador', C: DifareSimulador },
];
